import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from '../ui/card';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';

import {
  Sparkles,
  Check,
  ImageIcon,
  FileImage,
  Grid3X3,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  ArrowRight,
  Pencil,
  Palette
} from 'lucide-react';
import {
  buildAiFlyerRequest,
  buildManualRenderRequest,
  getMergeField
} from '../../utils/flyerGeneration';
import { generateAiFlyer, renderFlyer } from '../../services/flyerService';
import { API_POSTS_MONTH, APP_S3 } from '../../utils/constants';
import UserGallery from '../../pages/Calendar/GallerySection/UserGallery';
import ContentGallery from '../../pages/Calendar/GallerySection/ContentGallery';
import ClientGallery from '../../pages/Calendar/GallerySection/ClientGallery';
import GoogleBusinessGallery from '../../pages/Calendar/GallerySection/GoogleBusinessGallery';
import YelpGallery from '../../pages/Calendar/GallerySection/YelpGallery';
import Freepik from '../../pages/Calendar/GallerySection/StockImagesComponents/Freepik';
import Pixabay from '../../pages/Calendar/GallerySection/StockImagesComponents/Pixabay';
import { SearchContext } from '../../contexts/SearchContext';

const GALLERY_LABELS = [
  'App Gallery',
  'Content Gallery',
  'Client Gallery',
  'GBP Photos',
  'Yelp Gallery',
  'Freepik Gallery',
  'Pixabay Gallery'
];

const DEFAULT_FLYER_LAYOUT_COUNT = Math.max(
  Number(process.env.REACT_APP_FLYER_LAYOUT_COUNT || 9),
  1
);

const getWrappedIndex = (index, total) => {
  const safeTotal = Math.max(Number(total) || 1, 1);
  return ((index % safeTotal) + safeTotal) % safeTotal;
};

/**
 * CreateFlyerCard Component
 * Allows users to generate AI-powered flyers for their business
 */
const CreateFlyerCard = ({
  onFlyerGenerated,
  currentClientId,
  currentClientBrand,
  currentClientMergeFields,
  postsLists = [],
  currentClientLogoUrl = null
}) => {
  const currentClient = useSelector(
    (state) => state.selectedClient?.selectedClient
  );

  // Shared editable flyer form state
  const [title, setTitle] = useState('');
  const [mainFlyerText, setMainFlyerText] = useState('');
  const [details, setDetails] = useState('');
  const [phone, setPhone] = useState('');
  const [contactUsText, setContactUsText] = useState('Contact us today!');
  const [website, setWebsite] = useState('');

  // Generation counters (kept separate so backend can rotate templates independently)
  const [aiGenerationCount, setAiGenerationCount] = useState(0);
  const [selectedLayoutIndex, setSelectedLayoutIndex] = useState(0);
  const [layoutCount, setLayoutCount] = useState(DEFAULT_FLYER_LAYOUT_COUNT);

  // Loading states
  const [isGeneratingAiFlyer, setIsGeneratingAiFlyer] = useState(false);
  const [isRenderingManualFlyer, setIsRenderingManualFlyer] = useState(false);

  // Result states
  const [hasAiGeneratedFlyer, setHasAiGeneratedFlyer] = useState(false);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);

  // Mode — AI mode is active by default
  const [isAiMode, setIsAiMode] = useState(true);
  const [aiPrompt, setAiPrompt] = useState('');

  // Gallery
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [galleryQuery, setGalleryQuery] = useState('');
  const [refresh] = useState(false);

  // Brand options state
  const [useLogo, setUseLogo] = useState(!!currentClientLogoUrl);
  const [logoUrl, setLogoUrl] = useState(currentClientLogoUrl || null);
  const [usePrimaryColor, setUsePrimaryColor] = useState(true);
  const [primaryColor, setPrimaryColor] = useState(
    currentClientBrand?.primaryColor || '#3B5998'
  );
  const [useSecondaryColor, setUseSecondaryColor] = useState(true);
  const [secondaryColor, setSecondaryColor] = useState(
    currentClientBrand?.secondaryColor || '#8392A5'
  );

  // Image pool states (for flyer generation payload)
  const [fetchedPostImages, setFetchedPostImages] = useState([]);
  const [appGalleryImages, setAppGalleryImages] = useState([]);
  const [selectedImageFromGallery, setSelectedImageFromGallery] =
    useState(null);
  const activeRequestIdRef = useRef(0);

  // Prefill form fields when client changes
  useEffect(() => {
    const mergeFields =
      currentClientMergeFields || currentClient?.mergeFields || {};
    setTitle(currentClient?.clientName || '');
    setPhone(getMergeField(mergeFields, '~Phone'));
    setWebsite(getMergeField(mergeFields, '~Website'));
    setContactUsText('Contact us today!');
    setMainFlyerText('');
    setDetails('');
    setAiGenerationCount(0);
    setSelectedLayoutIndex(0);
    setLayoutCount(DEFAULT_FLYER_LAYOUT_COUNT);
    setHasAiGeneratedFlyer(false);
    setGeneratedImageUrl(null);
    setSelectedImageFromGallery(null);
    setAiPrompt('');
    setIsAiMode(true);
    setUseLogo(!!currentClientLogoUrl);
    setLogoUrl(currentClientLogoUrl || null);
    setUsePrimaryColor(true);
    setPrimaryColor(currentClientBrand?.primaryColor || '#3B5998');
    setUseSecondaryColor(true);
    setSecondaryColor(currentClientBrand?.secondaryColor || '#8392A5');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClientId]);

  // Fetch app gallery images directly from S3 (same source as UserGallery)
  useEffect(() => {
    const fetchAppGalleryImages = async () => {
      if (!currentClientId) return;
      try {
        const response = await axios.get(`${APP_S3}?userId=${currentClientId}`);
        const body = response.data?.body || [];
        const images = body.map((item) => ({
          url: (item.url || '').replace('/thumbnails', ''),
          source: 'app'
        }));
        setAppGalleryImages(images);
      } catch (err) {
        console.error('Error fetching app gallery images for flyer:', err);
      }
    };
    fetchAppGalleryImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClientId]);

  // Fetch current month's posts to build an image pool for the flyer
  useEffect(() => {
    const fetchPostImages = async () => {
      if (!currentClientId) return;
      try {
        const now = new Date();
        const res = await axios.post(API_POSTS_MONTH, {
          clientId: currentClientId,
          month: String(now.getMonth() + 1),
          year: String(now.getFullYear()),
          timeZone: currentClient?.timeZone || 'Eastern Daylight Time - (GMT-4)'
        });
        const posts = res.data?.posts || [];
        const images = [];
        posts.forEach((post) => {
          if (Array.isArray(post.images)) {
            post.images.forEach((img) => {
              const url = img.url || img.originalURL;
              if (url) images.push({ url, source: 'post' });
            });
          }
        });
        setFetchedPostImages(images);
      } catch (err) {
        console.error('Error fetching post images for flyer:', err);
      }
    };
    fetchPostImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentClientId]);

  // Memoized deduplicated image pool
  const imagePool = useMemo(() => {
    const seen = new Set();
    const pool = [];
    const tryAdd = (url, source) => {
      if (url && !seen.has(url)) {
        seen.add(url);
        pool.push({ url, source });
      }
    };
    fetchedPostImages.forEach(({ url }) => tryAdd(url, 'post'));
    appGalleryImages.forEach(({ url }) => tryAdd(url, 'app'));
    if (Array.isArray(postsLists)) {
      postsLists.forEach((post) => {
        if (Array.isArray(post.images)) {
          post.images.forEach((img) =>
            tryAdd(img.url || img.originalURL, 'post')
          );
        }
      });
    }
    (currentClient?.images || []).forEach((img) =>
      tryAdd(img.originalURL || img.url, 'library')
    );
    return pool;
  }, [fetchedPostImages, appGalleryImages, postsLists, currentClient?.images]);

  // Unified logo source
  const brandLogo =
    currentClientLogoUrl ||
    currentClientBrand?.logo ||
    currentClient?.logoUrl ||
    null;

  // Gallery navigation
  const handlePrevGallery = () => setGalleryIndex((i) => Math.max(0, i - 1));
  const handleNextGallery = () => setGalleryIndex((i) => Math.min(6, i + 1));

  // Gallery image selection callbacks
  const handleSetAppImage = (event) => {
    const url = event.target?.files?.[0]
      ? URL.createObjectURL(event.target.files[0])
      : event.target?.value;
    if (url) setSelectedImageFromGallery(url);
  };

  const handleCheckImg = (param) => {
    const url = param[1];
    if (url) setSelectedImageFromGallery(url);
  };

  const handleSelectImageFromGallery = (param) => {
    if (param && param.pk && param.sk) {
      setSelectedImageFromGallery(param.sk);
    } else if (Array.isArray(param) && param.length === 3) {
      handleCheckImg(param);
    } else {
      handleSetAppImage(param);
    }
  };

  // TODO: Remove FLYER_DRY_RUN before production release
  const FLYER_DRY_RUN = false;

  // Derived validation
  const allFlyerFieldsFilled = [
    title,
    mainFlyerText,
    details,
    phone,
    contactUsText,
    website
  ].every((f) => f.trim().length > 0);
  const canGenerateAi =
    !!aiPrompt.trim() && !!currentClientId && !!currentClient?.clientName;

  // AI generate+render: Gemini generates copy AND Bannerbear renders in one call.
  // On success: preload form fields, call onFlyerGenerated, switch to Manual mode.
  const handleAiGenerate = async () => {
    if (!canGenerateAi) return;
    const requestId = ++activeRequestIdRef.current;
    // Start at 0 so the first generation can use the first template.
    const currentCount = aiGenerationCount;
    setAiGenerationCount(currentCount + 1);
    setIsGeneratingAiFlyer(true);
    try {
      const mergeFields =
        currentClientMergeFields || currentClient?.mergeFields || {};

      const payload = buildAiFlyerRequest({
        clientId: currentClientId,
        aiPrompt: aiPrompt.trim(),
        clientContext: {
          clientName: currentClient?.clientName || null,
          phone: getMergeField(mergeFields, '~Phone') || null,
          website: getMergeField(mergeFields, '~Website') || null,
          logoUrl: brandLogo
        },
        imagePool,
        selectedImageUrl: selectedImageFromGallery,
        aiGenerationCount: currentCount,
        dryRun: FLYER_DRY_RUN,
        brandOptions: {
          useLogo,
          logoUrl,
          usePrimaryColor,
          primaryColor,
          useSecondaryColor,
          secondaryColor
        }
      });

      const result = await generateAiFlyer(payload);

      if (requestId !== activeRequestIdRef.current) {
        return;
      }

      if (result.success && result.dryRun) {
        alert('Dry run successful. Check console for Lambda output.');
        return;
      }

      // Preload editable form fields with AI-generated content
      if (result?.flyerContent) {
        const fc = result.flyerContent;
        setTitle(fc.title || '');
        setMainFlyerText(fc.mainFlyerText || '');
        setDetails(fc.details || '');
        setPhone(fc.phone || '');
        setContactUsText(fc.contactUsText || 'Contact us today!');
        setWebsite(fc.website || '');
      }

      if (result?.s3Url) {
        setGeneratedImageUrl(result.s3Url);
        setHasAiGeneratedFlyer(true);
        const backendLayoutCount = Number(result?.metadata?.totalTemplates);
        if (Number.isInteger(backendLayoutCount) && backendLayoutCount > 0) {
          setLayoutCount(backendLayoutCount);
        }
        const backendLayoutIndex = Number(
          result?.metadata?.selectedTemplateIndex
        );
        if (Number.isInteger(backendLayoutIndex)) {
          setSelectedLayoutIndex(
            getWrappedIndex(
              backendLayoutIndex,
              backendLayoutCount || layoutCount
            )
          );
        }
        if (onFlyerGenerated) onFlyerGenerated(result.s3Url);
        // Automatically switch to Manual/Edit mode so user can tweak fields
        setIsAiMode(false);
      }
    } catch (error) {
      alert(
        error.message || 'Failed to generate flyer with AI. Please try again.'
      );
      console.error('AI flyer generation error:', error);
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setIsGeneratingAiFlyer(false);
      }
    }
  };

  // Manual render: only Bannerbear is called using the current editable fields.
  const handleManualRender = async (
    overrideLayoutIndex,
    overrideSelectedImageUrl
  ) => {
    if (!allFlyerFieldsFilled) return;
    const requestId = ++activeRequestIdRef.current;
    setIsRenderingManualFlyer(true);
    try {
      const currentLayoutIndex =
        overrideLayoutIndex !== undefined
          ? overrideLayoutIndex
          : selectedLayoutIndex;

      const currentSelectedImageUrl =
        overrideSelectedImageUrl !== undefined
          ? overrideSelectedImageUrl
          : selectedImageFromGallery;

      const payload = buildManualRenderRequest({
        clientId: currentClientId,
        flyerContent: {
          title: title.trim(),
          mainFlyerText: mainFlyerText.trim(),
          details: details.trim(),
          phone: phone.trim(),
          contactUsText: contactUsText.trim(),
          website: website.trim()
        },
        clientContext: { logoUrl: brandLogo },
        imagePool,
        selectedImageUrl: currentSelectedImageUrl,
        manualRenderCount: currentLayoutIndex,
        dryRun: FLYER_DRY_RUN,
        brandOptions: {
          useLogo,
          logoUrl,
          usePrimaryColor,
          primaryColor,
          useSecondaryColor,
          secondaryColor
        }
      });

      const result = await renderFlyer(payload);

      if (requestId !== activeRequestIdRef.current) {
        return;
      }

      if (result.success && result.dryRun) {
        alert('Dry run successful. Check console for Lambda output.');
        return;
      }

      if (result.success && result.s3Url) {
        setGeneratedImageUrl(result.s3Url);
        const backendLayoutCount = Number(result?.metadata?.totalTemplates);
        if (Number.isInteger(backendLayoutCount) && backendLayoutCount > 0) {
          setLayoutCount(backendLayoutCount);
        }
        const backendLayoutIndex = Number(
          result?.metadata?.selectedTemplateIndex
        );
        if (Number.isInteger(backendLayoutIndex)) {
          setSelectedLayoutIndex(
            getWrappedIndex(
              backendLayoutIndex,
              backendLayoutCount || layoutCount
            )
          );
        }
        if (onFlyerGenerated) onFlyerGenerated(result.s3Url);
      }
    } catch (error) {
      alert(error.message || 'Failed to generate flyer. Please try again.');
      console.error('Flyer manual render error:', error);
    } finally {
      if (requestId === activeRequestIdRef.current) {
        setIsRenderingManualFlyer(false);
      }
    }
  };

  const handleChangeLayout = async (direction) => {
    if (isLoading) return;

    const nextIndex = getWrappedIndex(
      selectedLayoutIndex + direction,
      layoutCount
    );
    setSelectedLayoutIndex(nextIndex);

    // If fields are complete, re-render immediately when changing layout.
    if (allFlyerFieldsFilled) {
      await handleManualRender(
        nextIndex,
        selectedImageFromGallery ?? undefined
      );
    }
  };

  const isLoading = isGeneratingAiFlyer || isRenderingManualFlyer;

  // Only show the rendered flyer in the preview, never the bare gallery selection.
  const previewUrl = generatedImageUrl || null;

  return (
    <Card className="shadow-md rounded-2xl border-0 flex flex-col">
      <CardHeader className="pb-4 pt-6 px-6 lg:px-8">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
            <FileImage className="h-6 w-6 text-primary" />
          </div>
          <div>
            <CardTitle className="text-xl lg:text-2xl font-bold">
              Create Flyer
            </CardTitle>
            <CardDescription className="text-base mt-1">
              Design and generate AI-powered flyers
            </CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 px-6 lg:px-8 pb-8">
        {/* Image Preview */}
        <div className="border border-border rounded-2xl overflow-hidden bg-card">
          <div className="aspect-square bg-muted/30 flex items-center justify-center overflow-hidden">
            {isLoading ? (
              <div className="text-center text-muted-foreground p-6">
                <div className="h-16 w-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-base">Generating your flyer...</p>
              </div>
            ) : previewUrl ? (
              <img
                src={previewUrl}
                alt="Flyer Preview"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="text-center text-muted-foreground p-6">
                <ImageIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p className="text-base">No image preview</p>
              </div>
            )}
          </div>
        </div>

        {/* Flyer Layout arrows — shown in Manual mode only */}
        {!isAiMode && (
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">
              Flyer Layout
            </Label>
            <div className="flex items-center justify-center gap-4">
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => handleChangeLayout(-1)}
                aria-label="Previous layout"
                className="h-11 w-11 rounded-full shadow-md">
                <ChevronLeft className="h-5 w-5" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => handleChangeLayout(1)}
                aria-label="Next layout"
                className="h-11 w-11 rounded-full shadow-md">
                <ChevronRight className="h-5 w-5" />
              </Button>
            </div>
          </div>
        )}

        {/* AI / Manual Toggle */}
        <div className="flex items-center justify-between py-3 px-4 bg-muted/30 rounded-xl">
          <span
            className={`text-sm font-medium flex items-center gap-1.5 ${
              !isAiMode ? 'text-foreground' : 'text-muted-foreground'
            }`}>
            <Pencil className="h-4 w-4" />
            Edit Manual
          </span>
          <Switch checked={isAiMode} onCheckedChange={setIsAiMode} />
          <span
            className={`text-sm font-medium flex items-center gap-1.5 ${
              isAiMode ? 'text-foreground' : 'text-muted-foreground'
            }`}>
            <Sparkles className="h-4 w-4" />
            AI Generate
          </span>
        </div>

        {/* AI mode: prompt + generate button */}
        {isAiMode && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label
                htmlFor="aiPrompt"
                className="text-sm text-muted-foreground">
                Describe your flyer *
              </Label>
              <Textarea
                id="aiPrompt"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                className="min-h-[100px] resize-y text-base"
                placeholder="Describe the flyer you want to create, e.g. 'A summer sale flyer with bold colors and a 50% off banner'"
              />
            </div>
          </div>
        )}

        {/* Shared editable flyer form — shown in Manual mode only */}
        {!isAiMode && (
          <div className="space-y-4">
            {hasAiGeneratedFlyer && (
              <p className="text-sm text-muted-foreground">
                AI-generated fields — edit as needed
              </p>
            )}

            <div className="space-y-2">
              <Label htmlFor="flyerTitle" className="text-base font-medium">
                Title *
              </Label>
              <Input
                id="flyerTitle"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Business name or flyer title"
                maxLength={100}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="flyerMainText" className="text-base font-medium">
                Main flyer text *
              </Label>
              <Textarea
                id="flyerMainText"
                value={mainFlyerText}
                onChange={(e) => setMainFlyerText(e.target.value)}
                placeholder="Describe what this flyer is about"
                rows={3}
                maxLength={500}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground text-right">
                {mainFlyerText.length}/500
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="flyerDetails" className="text-base font-medium">
                Details *
              </Label>
              <Textarea
                id="flyerDetails"
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                placeholder="Enter additional details"
                rows={2}
                maxLength={300}
                className="resize-none"
              />
              <p className="text-xs text-muted-foreground text-right">
                {details.length}/300
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="flyerPhone" className="text-base font-medium">
                Phone *
              </Label>
              <Input
                id="flyerPhone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Business phone number"
                maxLength={50}
              />
            </div>

            {/* <div className="space-y-2">
              <Label
                htmlFor="flyerContactUsText"
                className="text-base font-medium">
                Call to Action CTA *
              </Label>
              <Input
                id="flyerContactUsText"
                value={contactUsText}
                onChange={(e) => setContactUsText(e.target.value)}
                placeholder="e.g. Contact us today!"
                maxLength={100}
              />
            </div> */}

            <div className="space-y-2">
              <Label htmlFor="flyerWebsite" className="text-base font-medium">
                Website *
              </Label>
              <Input
                id="flyerWebsite"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="Business website URL"
                maxLength={200}
              />
            </div>
          </div>
        )}

        {/* Brand Options — only shown in Manual mode; AI mode uses preloaded values silently */}
        {!isAiMode && (
          <>
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Palette className="h-5 w-5 text-primary" />
                <span className="text-base font-semibold">Brand Options</span>
              </div>

              {/* Use Logo */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Use Logo</Label>
                  <Switch checked={useLogo} onCheckedChange={setUseLogo} />
                </div>
                {useLogo && logoUrl && (
                  <div className="flex items-center gap-3 p-2 bg-muted/30 rounded-lg">
                    <img
                      src={logoUrl}
                      alt="Logo preview"
                      className="h-10 w-10 object-contain rounded"
                    />
                    <span className="text-xs text-muted-foreground truncate flex-1">
                      {logoUrl}
                    </span>
                  </div>
                )}
                {useLogo && (
                  <div className="space-y-1">
                    <Label
                      htmlFor="brandLogoUrl"
                      className="text-xs text-muted-foreground">
                      Logo URL {logoUrl ? '(optional override)' : '(required)'}
                    </Label>
                    <Input
                      id="brandLogoUrl"
                      value={logoUrl || ''}
                      onChange={(e) => setLogoUrl(e.target.value || null)}
                      placeholder="https://..."
                      className="text-sm"
                    />
                  </div>
                )}
              </div>

              {/* Primary Color */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Primary Color</Label>
                  <Switch
                    checked={usePrimaryColor}
                    onCheckedChange={setUsePrimaryColor}
                  />
                </div>
                {usePrimaryColor && (
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={primaryColor || '#3B5998'}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      className="h-10 w-10 rounded cursor-pointer border border-border"
                    />
                    <Input
                      value={primaryColor || '#3B5998'}
                      onChange={(e) => setPrimaryColor(e.target.value)}
                      placeholder="#3B5998"
                      maxLength={7}
                      className="text-sm flex-1"
                    />
                  </div>
                )}
              </div>

              {/* Secondary / Accent Color */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm font-medium">Accent Color</Label>
                  <Switch
                    checked={useSecondaryColor}
                    onCheckedChange={setUseSecondaryColor}
                  />
                </div>
                {useSecondaryColor && (
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={secondaryColor || '#8392A5'}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      className="h-10 w-10 rounded cursor-pointer border border-border"
                    />
                    <Input
                      value={secondaryColor || '#8392A5'}
                      onChange={(e) => setSecondaryColor(e.target.value)}
                      placeholder="#8392A5"
                      maxLength={7}
                      className="text-sm flex-1"
                    />
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Gallery Section — only shown in Manual mode */}
        {!isAiMode && (
          <>
            <Separator />
            <SearchContext.Provider
              value={{ query: galleryQuery, setQuery: setGalleryQuery }}>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Grid3X3 className="h-5 w-5 text-primary" />
                    <span className="text-base font-semibold">
                      Select Image
                    </span>
                  </div>
                  {selectedImageFromGallery && (
                    <span className="text-xs text-green-600 font-medium flex items-center gap-1">
                      <Check className="h-3 w-3" /> Reference selected
                    </span>
                  )}
                </div>
                {/* Nav arrows — fixed above the scrollable gallery */}
                <div className="flex items-center gap-3">
                  <Button
                    variant="outline"
                    size="icon"
                    type="button"
                    onClick={handlePrevGallery}
                    className="h-10 w-10 rounded-lg shrink-0"
                    disabled={galleryIndex === 0}>
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div className="flex-1 bg-muted/50 rounded-xl px-4 py-2.5 text-center">
                    <span className="text-sm font-medium">
                      {GALLERY_LABELS[galleryIndex]}
                    </span>
                    <span className="text-xs text-muted-foreground ml-2">
                      ({galleryIndex + 1}/7)
                    </span>
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    type="button"
                    onClick={handleNextGallery}
                    className="h-10 w-10 rounded-lg shrink-0"
                    disabled={galleryIndex === 6}>
                    <ArrowRight className="h-5 w-5" />
                  </Button>
                </div>
                {galleryIndex === 0 && (
                  <UserGallery
                    setAppImage={handleSetAppImage}
                    checkImg={handleCheckImg}
                    userId={currentClientId}
                    refresh={refresh}
                    maxHeight="400px"
                  />
                )}
                {galleryIndex === 1 && (
                  <ContentGallery
                    setAppImage={handleSetAppImage}
                    userId={currentClientId}
                    refresh={refresh}
                    maxHeight="400px"
                  />
                )}
                {galleryIndex === 2 && (
                  <ClientGallery
                    setAppImage={handleSetAppImage}
                    userId={currentClientId}
                    refresh={refresh}
                    maxHeight="400px"
                  />
                )}
                {galleryIndex === 3 && (
                  <GoogleBusinessGallery
                    setAppImage={handleSelectImageFromGallery}
                    userId={currentClientId}
                    refresh={refresh}
                    maxHeight="400px"
                  />
                )}
                {galleryIndex === 4 && (
                  <YelpGallery
                    setAppImage={handleSelectImageFromGallery}
                    userId={currentClientId}
                    refresh={refresh}
                    maxHeight="400px"
                  />
                )}
                {galleryIndex === 5 && (
                  <Freepik setAppImage={handleSetAppImage} maxHeight="400px" />
                )}
                {galleryIndex === 6 && (
                  <Pixabay setAppImage={handleSetAppImage} maxHeight="400px" />
                )}
              </div>
            </SearchContext.Provider>
          </>
        )}

        <Separator />

        {/* Generate Flyer button — AI mode triggers Gemini+Bannerbear; Manual mode triggers Bannerbear only */}
        <Button
          type="button"
          onClick={isAiMode ? handleAiGenerate : () => handleManualRender()}
          disabled={
            isAiMode
              ? isGeneratingAiFlyer || !canGenerateAi
              : isRenderingManualFlyer || !allFlyerFieldsFilled
          }
          className="w-full h-14 gap-3 bg-gradient-to-r from-gray-700 to-gray-900 hover:from-gray-800 hover:to-black text-white text-lg font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all">
          {isLoading ? (
            <>
              <div className="h-5 w-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              Generating flyer...
            </>
          ) : (
            <>
              <Sparkles className="h-6 w-6" />
              Generate Flyer
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};

export default CreateFlyerCard;
